/**
 * Shared presentation rules for the reservation Email Activity box.
 *
 * The activity list is Firestore-backed; body preview and resend remain
 * provider-aware callables. Raw infrastructure/provider failures do not belong
 * in the DOM, and a failed fetch must never be presented as "this reservation
 * has no email activity".
 */

const EMAIL_ACTIVITY_UNAVAILABLE_MESSAGE =
  "Email activity is temporarily unavailable. Sorry for the inconvenience.";

const EMAIL_DETAIL_UNAVAILABLE_MESSAGE =
  "Email preview is temporarily unavailable. Sorry for the inconvenience.";

const EMAIL_RESEND_UNAVAILABLE_MESSAGE =
  "Resending email is temporarily unavailable. Sorry for the inconvenience.";

const EMAIL_ACTIVITY_EMPTY_MESSAGE =
  "No email activity has been recorded for this reservation.";

/**
 * Callable error codes raised by our own backend preconditions. Their messages
 * ("Not authorized.", "Reservation doesn't exist.") are written for the user and
 * are actionable, so they are shown as-is. Everything else — including the
 * `internal` code that infrastructure/provider failures normalize to — is replaced
 * with product copy.
 */
const PASS_THROUGH_CODES = new Set([
  "failed-precondition",
  "unauthenticated",
  "permission-denied",
]);

const normalizeCode = (error) => {
  const code = String(error?.code || "");
  return code.includes("/") ? code.slice(code.lastIndexOf("/") + 1) : code;
};

/**
 * Normalizes a thrown callable error into the shape the reservation slice stores.
 *
 * `message` is always safe to render. `detail` keeps the raw upstream text for
 * console/LogRocket diagnostics only — never render it.
 */
const toEmailActivityError = (
  error,
  fallbackMessage = EMAIL_ACTIVITY_UNAVAILABLE_MESSAGE
) => {
  const code = normalizeCode(error);
  const detail = typeof error?.message === "string" ? error.message.trim() : "";
  const passThrough = PASS_THROUGH_CODES.has(code) && detail.length > 0;

  return {
    code: code || "unknown",
    unavailable: !passThrough,
    message: passThrough ? detail : fallbackMessage,
    detail,
  };
};

/**
 * Tolerates legacy/plain-string error values still held in state so the box
 * never renders `undefined` or raw upstream text.
 */
const asEmailActivityError = (
  value,
  fallbackMessage = EMAIL_ACTIVITY_UNAVAILABLE_MESSAGE
) => {
  if (!value) return null;
  if (typeof value === "object" && typeof value.message === "string") {
    return {
      code: value.code || "unknown",
      unavailable: value.unavailable !== false,
      message: value.message,
      detail: typeof value.detail === "string" ? value.detail : "",
    };
  }
  return toEmailActivityError({ message: value }, fallbackMessage);
};

const EMAIL_ACTIVITY_STATE = {
  LOADING: "loading",
  UNAVAILABLE: "unavailable",
  BLOCKED: "blocked",
  EMPTY: "empty",
  LIST: "list",
};

/**
 * Single source of truth for which Email Activity state renders.
 *
 * `UNAVAILABLE` (upstream failure) is deliberately distinct from `EMPTY`
 * (fetch succeeded, no messages) and from `BLOCKED` (our own backend refused
 * the request and said why).
 */
const resolveEmailActivityState = ({ loading, error, emails } = {}) => {
  if (loading) return EMAIL_ACTIVITY_STATE.LOADING;

  const normalized = asEmailActivityError(error);
  if (normalized) {
    return normalized.unavailable
      ? EMAIL_ACTIVITY_STATE.UNAVAILABLE
      : EMAIL_ACTIVITY_STATE.BLOCKED;
  }

  return Array.isArray(emails) && emails.length > 0
    ? EMAIL_ACTIVITY_STATE.LIST
    : EMAIL_ACTIVITY_STATE.EMPTY;
};

module.exports = {
  EMAIL_ACTIVITY_UNAVAILABLE_MESSAGE,
  EMAIL_DETAIL_UNAVAILABLE_MESSAGE,
  EMAIL_RESEND_UNAVAILABLE_MESSAGE,
  EMAIL_ACTIVITY_EMPTY_MESSAGE,
  EMAIL_ACTIVITY_STATE,
  toEmailActivityError,
  asEmailActivityError,
  resolveEmailActivityState,
};
